import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, PlayCircle } from "lucide-react";

/**
 * TutorialOverlay — lightweight, dismissible walkthrough.
 * Auto-opens once per user role on first login (localStorage flag `asap:tutorial:<role>`).
 * Restart by clicking the "Tutorial" button in the header.
 */
const CUSTOMER_STEPS = [
  { title: "Welcome to ASAP Roadside", body: "This 30-second tour shows you exactly how to get help when you're stuck." },
  { title: "1 · Pick a service", body: "Tap 'Request Service' and choose Tow, Jump, Tire, Fuel, Lockout, or Winch. Prices are flat and shown up front." },
  { title: "2 · Drop your pin", body: "Tap the map at your exact location, add your vehicle info, and any notes (wrong side of the highway, in a ditch, etc.)." },
  { title: "3 · Pay only after acceptance", body: "You're not charged until a driver accepts. Add a tip at checkout — 100% goes to the driver." },
  { title: "4 · Live tracking + SMS", body: "Watch your driver's truck icon move on the map with a live ETA. You get texts on every status change." },
  { title: "5 · Rate + Receipt", body: "After the job, rate the driver (1-5 ★) and grab a printable receipt. That's it." },
];
const DRIVER_STEPS = [
  { title: "Welcome, driver", body: "Two-minute walk-through of your daily flow — from getting approved to getting paid." },
  { title: "1 · Upload your docs", body: "Go to Documents → upload your driver's license and insurance. An admin will approve you within minutes." },
  { title: "2 · Set your vehicle + capabilities", body: "Pick your rig type (car, winch truck, flatbed, wheel-lift, heavy wrecker). You'll only be paged for jobs your rig can actually handle." },
  { title: "3 · Join a zone & flip Online", body: "ASAP serves zones nationwide (15–30 mile radius). Each zone has 7 founding slots (free) then $29/mo. Flip the Online toggle to start receiving requests." },
  { title: "4 · Vrooom = new job", body: "When a request lands, the roadrunner dashes across your screen with a 'vrooom' sound. Tap Accept within the countdown." },
  { title: "5 · Advance the status", body: "En route → On scene → Completed. Each tap texts the customer automatically." },
  { title: "6 · Get paid", body: "Connect Stripe once in Payouts. You get 70% of the job + 100% of the tip, cashed out to your bank instantly after each completed trip." },
];

export default function TutorialOverlay({ role, forceOpen, onClose }) {
  const key = `asap:tutorial:${role}`;
  const steps = role === "driver" ? DRIVER_STEPS : CUSTOMER_STEPS;
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true); setIdx(0);
    } else {
      try {
        const seen = localStorage.getItem(key);
        if (!seen) { setOpen(true); setIdx(0); }
      } catch {}
    }
  }, [forceOpen, key]);

  const close = () => {
    try { localStorage.setItem(key, "seen"); } catch {}
    setOpen(false);
    onClose?.();
  };

  if (!open) return null;
  const step = steps[idx];
  const isLast = idx === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="tutorial-overlay" onClick={close}>
      <div className="bg-white border-2 border-asap-deep shadow-hard w-full sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 bg-asap-deep text-white flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <PlayCircle size={16} className="shrink-0 text-asap-yellow"/>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold truncate">{role === "driver" ? "Driver Walk-through" : "Customer Walk-through"}</div>
          </div>
          <button onClick={close} data-testid="tutorial-close" aria-label="Close tutorial" className="p-1 hover:bg-white/20"><X size={16}/></button>
        </div>
        <div className="p-5">
          <div className="flex gap-1 mb-4" data-testid="tutorial-progress">
            {steps.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 ${i <= idx ? "bg-asap-orange" : "bg-slate-200"}`} />
            ))}
          </div>
          <h3 className="font-display text-2xl uppercase text-asap-deep leading-tight">{step.title}</h3>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed">{step.body}</p>
          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={() => idx > 0 && setIdx(idx - 1)}
              disabled={idx === 0}
              data-testid="tutorial-back"
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-asap-deep hover:bg-slate-100 disabled:opacity-30 disabled:pointer-events-none flex items-center gap-1"
            >
              <ArrowLeft size={12}/> Back
            </button>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Step {idx + 1} of {steps.length}</div>
            {!isLast ? (
              <button onClick={() => setIdx(idx + 1)} data-testid="tutorial-next" className="px-4 py-2 bg-asap-yellow text-asap-deep text-xs font-bold uppercase tracking-wider border-2 border-asap-deep flex items-center gap-1 hover:translate-x-[1px] hover:translate-y-[1px] transition">
                Next <ArrowRight size={12}/>
              </button>
            ) : (
              <button onClick={close} data-testid="tutorial-finish" className="px-4 py-2 bg-asap-orange text-white text-xs font-bold uppercase tracking-wider border-2 border-asap-deep flex items-center gap-1 hover:translate-x-[1px] hover:translate-y-[1px] transition">
                Got it
              </button>
            )}
          </div>
          <div className="mt-4 text-center">
            <Link to="/how-it-works" onClick={close} data-testid="tutorial-full-guide" className="text-[10px] uppercase tracking-widest text-asap-orange hover:text-asap-deep font-bold">Open the full How It Works guide →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
